import { useState, useRef, useEffect } from "react";
import { useApp } from "@/App";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import axios from "axios";

export default function ChatBot() {
  const { API } = useApp();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "ai",
      text: "Ahoj! Jsem Car Spa poradce. Rád vám pomohu vybrat ideální program péče o vaše auto. Jakou značku a model vozidla máte?"
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const messagesEnd = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: userMsg }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: userMsg
      });
      setMessages(prev => [...prev, { role: "ai", text: res.data.reply }]);
    } catch {
      setMessages(prev => [...prev, {
        role: "ai",
        text: "Omlouvám se, momentálně nemohu odpovědět. Zkuste to prosím znovu nebo nás kontaktujte přímo."
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Toggle button */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", duration: 0.5 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-20 right-6 z-50 w-14 h-14 bg-[#39FF14] text-black flex items-center justify-center neon-glow-strong hover:bg-white transition-colors duration-300"
            data-testid="chatbot-toggle"
          >
            <MessageCircle className="w-6 h-6" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-20 right-6 z-[60] w-[380px] max-w-[calc(100vw-3rem)] h-[520px] flex flex-col glass-strong"
            data-testid="chatbot-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#39FF14]/20 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-[#39FF14]" />
                </div>
                <div>
                  <p className="text-sm font-bold">Car Spa AI Poradce</p>
                  <p className="text-[10px] text-[#39FF14] tracking-wider uppercase">Online</p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-zinc-500 hover:text-white transition-colors duration-300"
                data-testid="chatbot-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chatbot-messages">
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                      msg.role === "user"
                        ? "chat-bubble-user text-white"
                        : "chat-bubble-ai text-zinc-300"
                    }`}
                  >
                    {msg.text}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="chat-bubble-ai px-4 py-3 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 text-[#39FF14] animate-spin" />
                    <span className="text-xs text-zinc-500">Přemýšlím...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEnd} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-white/5">
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Napište zprávu..."
                  className="flex-1 bg-white/5 border border-white/10 text-white text-sm px-4 py-3 focus:outline-none focus:border-[#39FF14] placeholder:text-zinc-600"
                  data-testid="chatbot-input"
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim() || loading}
                  className={`w-11 h-11 flex items-center justify-center transition-colors duration-300 ${
                    input.trim() && !loading
                      ? "bg-[#39FF14] text-black hover:bg-white"
                      : "bg-white/5 text-zinc-600"
                  }`}
                  data-testid="chatbot-send"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
